import { ApiError, classify, type ApiErrorBody } from "./errors";

/**
 * THE CHOKE POINT. Every backend call in AnkhOS goes through here.
 *
 * There is exactly one of these on purpose. The PHP portal reaches ten backends
 * through one function (inc/apiclient.php), which is the single reason adding a
 * credential there is a one-line change rather than a 192-site migration. The
 * same property is worth having from day one rather than retrofitting it.
 */

/** Response headers radreportsapi uses to announce the scope it actually ran with. */
export const SCOPE_HEADER = "x-report-scope";
export const SCOPE_NARROWED_HEADER = "x-report-scope-narrowed";

export interface CallerIdentity {
  /** The operator's access token. */
  token: string | null;
  /** The namespace resolved FROM THAT TOKEN. Never from the UI. */
  namespace: string | null;
}

export interface GatewayResult<T> {
  data: T;
  /**
   * The scope the answer actually covers.
   *
   * ⚠️ radreportsapi implements Contract B (NARROW): asking wider than you are
   * entitled to is NOT refused, it is narrowed to your authorized root and
   * answered 200. Only a DISJOINT subtree is 403. A narrowed report is
   * otherwise indistinguishable from one that simply had no such rows, which is
   * why the server announces it and why the UI must surface it.
   *
   * 🪤 Reading this requires Access-Control-Expose-Headers on the gateway.
   * radaccess did not send it -- config/server.go had ExposedHeaders and
   * app/app.go never passed it to cors.Options -- so in a browser these read
   * null while being perfectly readable from Node/curl. Fixed in radaccess
   * 9f69ba6, live since v1.23.
   */
  scope: string | null;
  narrowed: boolean;
}

export interface CallOptions {
  method?: "GET" | "HEAD" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * 🔑 FAIL CLOSED. If there is no token or no namespace, the request is NOT
 * SENT — we do not send it and let the backend decide.
 *
 * The reason is not politeness. A request sent without a resolved namespace is
 * a request whose tenant the SERVER has to guess, and the failure mode of
 * guessing is cross-tenant data. Refusing locally makes the missing-namespace
 * case loud and local instead of silent and remote.
 */
function requireIdentity(identity: CallerIdentity): asserts identity is { token: string; namespace: string } {
  if (!identity.token) {
    throw new ApiError("fail-closed", "No access token: the request was not sent.");
  }
  if (!identity.namespace) {
    throw new ApiError(
      "fail-closed",
      "No namespace could be resolved from the token: the request was not sent. " +
        "The token is missing both the flat `namespace` claim and the base64 " +
        "`urn:zitadel:iam:user:metadata` claim — check the SPA's scope set.",
    );
  }
}

async function failure(res: Response, path: string): Promise<ApiError> {
  let body: Partial<ApiErrorBody> = {};
  try {
    body = (await res.json()) as Partial<ApiErrorBody>;
  } catch {
    /* a non-JSON error body is still an error; keep the status */
  }
  return new ApiError(
    classify(res.status),
    body.error ?? `HTTP ${res.status} from ${path}`,
    res.status,
    body.retry_after ?? null,
    body,
  );
}

/** A fetch() that threw: the request may still have reached the server. */
export function networkFailureMessage(path: string, cause: unknown): string {
  return `Network failure calling ${path}: ${String(cause)} — the server may be reachable but its answer was blocked on the way back`;
}

export async function callGateway<T>(
  identity: CallerIdentity,
  base: string,
  path: string,
  opts: CallOptions = {},
): Promise<GatewayResult<T>> {
  requireIdentity(identity);

  const method = opts.method ?? "GET";

  // 🚨 A GET can never carry a body. The Fetch standard throws a TypeError for
  // that combination, which is precisely why radreportsapi's GET-with-a-JSON-
  // body contract is unreachable from a browser and needs POST aliases. Guard
  // it here so the failure names the real cause instead of surfacing as an
  // opaque TypeError from fetch().
  if (opts.body !== undefined && (method === "GET" || method === "HEAD")) {
    throw new ApiError(
      "fail-closed",
      `Cannot send a request body with ${method}: the Fetch standard forbids it. ` +
        "This endpoint needs a POST alias on the backend.",
    );
  }

  const headers: Record<string, string> = { Accept: "application/json" };
  headers["Authorization"] = `Bearer ${identity.token}`;
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";

  let res: Response;
  try {
    res = await fetch(`${base}${path}`, {
      method,
      headers,
      ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (cause) {
    // 🪤 "Failed to fetch" does not mean the server was unreachable: Cloudflare
    // in front of access.ankhos.io can block the answer (an origin 502, a CORS-
    // less error page) after the request has landed. Say so.
    throw new ApiError("unavailable", networkFailureMessage(path, cause));
  }

  if (!res.ok) throw await failure(res, path);

  const scope = res.headers.get(SCOPE_HEADER);
  const narrowed = res.headers.get(SCOPE_NARROWED_HEADER) === "true";

  // 🪤 204 IS A SUCCESS, AND IT HAS NO BODY. res.ok is true for it, so falling
  // straight into res.json() throws "Unexpected end of JSON input" and the
  // caller sees a parse error where the server said, correctly, "nothing here".
  //
  // This is not hypothetical: radpolicy answers 204 for a group that has no
  // attributes yet (app/handler/radgrouppolicy.go:551), and several services in
  // this estate do the same for an empty list. Treat it as the empty answer it
  // is. Callers expecting a list get [], callers expecting an object get null,
  // and either way `scope` still carries what the server said it ran with.
  if (res.status === 204 || res.headers.get("Content-Length") === "0") {
    return { data: null as T, scope, narrowed };
  }

  const data = (await res.json()) as T;
  return { data, scope, narrowed };
}

/**
 * A GET whose answer is bytes -- an image or a PDF -- rather than JSON. Same
 * choke point, same fail-closed identity rule, same error shape as callGateway.
 */
/** A multipart upload. The browser sets the Content-Type with its boundary. */
export async function callGatewayForm<T>(identity: CallerIdentity, base: string, path: string, form: FormData): Promise<T> {
  requireIdentity(identity);
  let res: Response;
  try {
    res = await fetch(`${base}${path}`, {
      method: "POST",
      headers: { Accept: "application/json", Authorization: `Bearer ${identity.token}` },
      body: form,
    });
  } catch (cause) {
    throw new ApiError("unavailable", networkFailureMessage(path, cause));
  }
  if (!res.ok) throw await failure(res, path);
  return (await res.json()) as T;
}

export async function callGatewayBytes(
  identity: CallerIdentity,
  base: string,
  path: string,
  opts: { signal?: AbortSignal; accept?: string } = {},
): Promise<Blob> {
  requireIdentity(identity);
  let res: Response;
  try {
    res = await fetch(`${base}${path}`, {
      method: "GET",
      headers: { Accept: opts.accept ?? "image/png, image/jpeg", Authorization: `Bearer ${identity.token}` },
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (cause) {
    throw new ApiError("unavailable", `Network failure calling ${path}: ${String(cause)}`);
  }
  if (!res.ok) throw await failure(res, path);
  return res.blob();
}
